import type { Tutorial } from '@/shared/components/tutorial/types'

/**
 * Context for the owner first-steps tutorial. The picker passes the
 * timestamp signals from `club` plus the staff count so the tutorial
 * primitive stays free of cross-feature fetching.
 */
export interface OwnerTutorialCtx {
  /** Owner has explicitly confirmed the opening hours. */
  hoursReviewed: boolean
  /** True once `club.courtsReviewedAt` is set — owner has explicitly confirmed
   * their courts. */
  courtsReviewed: boolean
  /** True once `club.pricesReviewedAt` is set — owner has touched
   * pricing (defaults panel save or a per-court rule edit). */
  pricesReviewed: boolean
  /** True once `club.catalogReviewedAt` is set — owner has opened the
   * Catálogo (products & services) page at least once. */
  catalogReviewed: boolean
  /** True once `club.analyticsViewedAt` is set — owner has opened the
   * Rendimiento page at least once. */
  analyticsReviewed: boolean
  /** Active staff docs (owner + invited staff). */
  staffCount: number
}

/**
 * Owner first-run tutorial. Uses operational signals (real Firestore
 * field stamps), not config-step checks: every club is admin-pre-built
 * under the Phase-1 white-glove model, so config steps would auto-tick
 * on the admin pre-fill and leave the owner with no orientation.
 *
 * Six steps. Order follows the owner's mental model of standing the club up:
 * capacity (pistas) → availability (horario) → price it (precios) →
 * what else you sell (productos/servicios) → is it
 * working (rendimiento) → hand the desk over (recepción). Install +
 * install is housekeeping (it stays on the reception tour, where the
 * tablet-at-the-desk operator needs it). Horario is included because
 * self-serve clubs receive a starter schedule that needs owner review.
 * Rendimiento was previously cut "meaningless without data", but
 * the stamp fires on first OPEN regardless of data, so it orients the
 * owner toward his #1 screen (occupancy + revenue) without waiting for
 * a populated club.
 */
export const OWNER_TUTORIAL: Tutorial<OwnerTutorialCtx> = {
  id: 'owner',
  title: 'Tus primeros pasos',
  intro:
    'Revisa lo esencial para arrancar y prepara el club a tu ritmo.',
  steps: [
    {
      id: 'review-courts',
      label: 'Revisa tus pistas',
      hint: 'Comprueba los nombres, la superficie y qué pistas están disponibles. Guarda o confirma la configuración cuando sea correcta.',
      isComplete: ({ courtsReviewed }) => courtsReviewed,
      deepLink: { label: 'Ir a Pistas', to: '/courts' },
    },
    {
      id: 'review-hours',
      label: 'Revisa tu horario',
      hint: 'Comprueba los días y horas de apertura, cierres y festivos. Guarda o confirma el horario cuando sea correcto.',
      isComplete: ({ hoursReviewed }) => hoursReviewed,
      deepLink: { label: 'Ir a Horario', to: '/settings?tab=hours' },
    },
    {
      id: 'review-prices',
      label: 'Revisa precios',
      hint: 'Revisa el precio base, la duración de las reservas y las reglas por franja. Guarda los cambios o confirma los precios cuando sean correctos.',
      isComplete: ({ pricesReviewed }) => pricesReviewed,
      deepLink: { label: 'Ir a Precios', to: '/settings?tab=precios' },
    },
    {
      id: 'review-catalog',
      label: 'Productos y servicios',
      hint: 'Explora el catálogo. Podrás añadir productos y servicios, como bolas, alquiler de pala o bebidas. Este paso se completa al abrir la página.',
      isComplete: ({ catalogReviewed }) => catalogReviewed,
      deepLink: { label: 'Ir a Catálogo', to: '/catalogo' },
    },
    {
      id: 'review-analytics',
      label: 'Mira tu negocio',
      hint: 'Explora los datos de ocupación e ingresos. Al principio puede que aún no haya actividad. Este paso se completa al abrir la página.',
      isComplete: ({ analyticsReviewed }) => analyticsReviewed,
      deepLink: { label: 'Ir a Rendimiento', to: '/analytics' },
    },
    {
      id: 'invite-staff',
      label: 'Invita a tu recepción',
      hint: 'Invita a quien gestione el club contigo. Recibirá un email para acceder con su cuenta. Este paso se completa cuando haya otra persona activa en el equipo.',
      isComplete: ({ staffCount }) => staffCount >= 2,
      deepLink: { label: 'Ir a Personal', to: '/staff' },
    },
  ],
}
